import { VFC, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableSortLabel,
  TableBody,
  TablePagination,
  TableCell,
} from "@mui/material";

import { Tag } from "../../../entity/Tag";
import { LOCK_STATUS } from "../../../entity/MdmUser";
import { ApplicationProtectedRouting } from "../../../Routing";
import { TenantSearchResponse } from "../../tenant/api/TenantForm";
import { Utility } from "../../../utils/Utility";
import { DeviceUiConverter } from "../../../entity/Tenant";

/***************************************************************
 * ヘッダー
 **************************************************************/
interface HeadCell {
  key: keyof TenantSearchResponse;
  value: string;
  isSort?: Boolean;
}
const headCells: readonly HeadCell[] = [
  {
    key: "disclosureCode",
    value: "テナントコード",
    isSort: true,
  },
  {
    key: "tenantName",
    value: "テナント名",
    isSort: true,
  },
  {
    key: "companyName",
    value: "企業名",
    isSort: true,
  },
  {
    key: "parentName",
    value: "親テナント名",
    isSort: true,
  },
  {
    key: "upperOperation",
    value: "上位からの操作",
    isSort: true,
  },
  {
    key: "salesRepresentativeName",
    value: "営業担当者名",
    isSort: true,
  },
  {
    key: "tenantStatus",
    value: "ステータス",
    isSort: true,
  },
  {
    key: "contractStart",
    value: "テナント契約開始日",
    isSort: true,
  },
  {
    key: "contractEnd",
    value: "テナント契約終了日",
    isSort: true,
  },
];
interface HeadTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof TenantSearchResponse
  ) => void;
  order: Order;
  orderBy: string;
}

/***************************************************************
 * ソート
 **************************************************************/
type Order = "asc" | "desc";
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: string | Tag[] },
  b: { [key in Key]: string | Tag[] }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/***************************************************************
 * ヘッダー - レイアウト
 **************************************************************/
const Head = (props: HeadTableProps) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof TenantSearchResponse) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((header, index: number) => (
          <TableCell key={index}>
            {header.isSort ? (
              <TableSortLabel
                active={orderBy === header.key}
                direction={orderBy === header.key ? order : "asc"}
                onClick={createSortHandler(header.key)}
              >
                {header.value}
              </TableSortLabel>
            ) : (
              header.value
            )}
          </TableCell>
        ))}
        <TableCell key={headCells.length}></TableCell>
      </TableRow>
    </TableHead>
  );
};

/***************************************************************
 * 一覧 - レイアウト
 **************************************************************/
type Props = { tenantList: TenantSearchResponse[] };
export const TenantTable: VFC<Props> = ({ tenantList: tenantList }) => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof TenantSearchResponse>("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof TenantSearchResponse
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /***************************************************************
   * ページング処理
   **************************************************************/
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tenantList.length) : 0;
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /***************************************************************
   * 詳細
   **************************************************************/
  // 詳細ボタン押下
  const detailBtnClick = (id: string) => {
    sessionStorage.setItem("TENANT_ID", id);
    navigate(ApplicationProtectedRouting.tenantDetail.path());
  };

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table>
          <Head
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(tenantList, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((tenant, index) => {
                return (
                  <TableRow key={index}>
                    {/* <TableCell component="th" scope="row" padding="none"> */}
                    <TableCell>{tenant.disclosureCode}</TableCell>
                    <TableCell>{DeviceUiConverter.getDeletedTenantNameString({tenantName: tenant.tenantName, isDeleted: !!tenant.deleteDate})}</TableCell>
                    <TableCell>{tenant.companyName}</TableCell>
                    <TableCell>{tenant.parentName}</TableCell>
                    <TableCell>{tenant.upperOperation}</TableCell>
                    <TableCell>{tenant.salesRepresentativeName}</TableCell>
                    <TableCell>{tenant.tenantStatus}</TableCell>
                    <TableCell>{Utility.dateFormat(tenant.contractStart)}</TableCell>
                    <TableCell>{Utility.dateFormat(tenant.contractEnd)}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        onClick={() => detailBtnClick(tenant.id)}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[15, 30, 50]}
        component="div"
        count={tenantList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};
